/** Reglas puras de las sedes adicionales del paso Datos (multi-sede).
 *
 * La sede principal sigue viviendo en venue_name/google_maps_link del
 * torneo; estas filas son SOLO las sedes extra. El nombre de sede es la
 * llave con la que bloqueos y programación correlacionan, por eso aquí se
 * valida no-vacío y sin duplicados (incluida la principal) ANTES de llamar
 * al backend — que repite la misma validación fail-closed. */

import type { TournamentVenue, TournamentVenueInput } from "./api";

export interface VenueRowDraft {
  name: string;
  google_maps_link: string;
  court_count: string;
}

export function emptyVenueRow(): VenueRowDraft {
  return { name: "", google_maps_link: "", court_count: "" };
}

/** Valor del hecho "Sede" en la página pública (2ª ronda gate #156).
 *
 * convocatoria.venue puede venir AUTO-COPIADA desde la sede principal (el
 * paso Convocatoria la pre-llena), así que solo cuenta como override manual
 * REAL cuando difiere de la principal. Sin override real y con sedes
 * múltiples, se listan TODAS — ese es el contrato del feature. */
export function publicVenueFactValue(options: {
  convocatoriaVenue: string | null | undefined;
  primaryVenueName: string | null | undefined;
  venues: TournamentVenue[] | undefined;
}): string | null {
  const manual = (options.convocatoriaVenue ?? "").trim();
  const primary = (options.primaryVenueName ?? "").trim();
  const venues = options.venues ?? [];
  const isRealOverride = Boolean(manual) && manual.toLowerCase() !== primary.toLowerCase();
  if (isRealOverride) return manual;
  if (venues.length > 1) return venues.map((venue) => venue.name).join(" · ");
  return manual || primary || null;
}

export type VenueRowsResult =
  | { ok: true; venues: TournamentVenueInput[] }
  | { ok: false; error: string };

/** Convierte las filas del formulario al payload del PUT. Filas totalmente
 * vacías se descartan en silencio (el admin agregó una fila y se arrepintió);
 * una fila con datos pero sin nombre, o un nombre repetido, es error. */
export function prepareAdditionalVenues(
  rows: VenueRowDraft[],
  primaryVenueName: string,
): VenueRowsResult {
  const seen = new Set<string>();
  const primary = primaryVenueName.trim().toLowerCase();
  if (primary) seen.add(primary);
  const venues: TournamentVenueInput[] = [];
  for (const row of rows) {
    const name = row.name.trim();
    const link = row.google_maps_link.trim();
    const countRaw = row.court_count.trim();
    if (!name && !link && !countRaw) continue;
    if (!name) {
      return { ok: false, error: "Cada sede adicional necesita un nombre." };
    }
    const key = name.toLowerCase();
    if (seen.has(key)) {
      return {
        ok: false,
        error: `La sede «${name}» está repetida (o coincide con la sede principal).`,
      };
    }
    seen.add(key);
    let court_count: number | null = null;
    if (countRaw) {
      const parsed = Number(countRaw);
      if (!Number.isInteger(parsed) || parsed < 1 || parsed > 40) {
        return {
          ok: false,
          error: `Las canchas de «${name}» deben ser un número entre 1 y 40.`,
        };
      }
      court_count = parsed;
    }
    venues.push({ name, google_maps_link: link || null, court_count });
  }
  return { ok: true, venues };
}
